import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { CITIES, CATEGORIES, CATEGORY_ICONS } from "@/lib/constants";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import PhotoInput from "@/components/PhotoInput";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import PushToggle from "@/components/PushToggle";
import { MAX_MERCHANT_CATEGORIES } from "@/lib/constants";

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const { t, tCat, tCity } = useLang();
  const navigate = useNavigate();
  const isMerchant = user?.role === "merchant";
  const [form, setForm] = useState({
    business_name: user?.business_name || "",
    city: user?.city || "",
    categories: user?.categories || [],
    whatsapp: user?.whatsapp || "",
    description: user?.description || "",
    logo: user?.logo || null,
  });
  const [busy, setBusy] = useState(false);

  const toggleCat = (c) => setForm((f) => {
    if (f.categories.includes(c)) return { ...f, categories: f.categories.filter((x) => x !== c) };
    if (f.categories.length >= MAX_MERCHANT_CATEGORIES) {
      toast.error(t("maxTwoCategories"));
      return f;
    }
    return { ...f, categories: [...f.categories, c] };
  });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/merchant/profile", form); await refresh(); toast.success(t("profileSaved")); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="app-shell pb-24" data-testid="profile-page">
      <TopBar title={t("profile")} back={false} />
      <div className="px-5 pt-4">
        <div className="card-surface p-4 flex items-center gap-3">
          {form.logo ? <img src={form.logo} alt="" className="h-12 w-12 rounded-xl object-cover" />
            : <div className="h-12 w-12 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center font-bold">{(user?.name || "?")[0]}</div>}
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          </div>
          <span className="pill bg-zinc-100 text-zinc-700">{t(user?.role)}</span>
        </div>

        <div className="mt-5">
          <PushToggle />
        </div>

        {isMerchant && (
          <form onSubmit={save} className="mt-5 space-y-4">
            <div>
              <label className="label-caps mb-1.5 block">{t("businessName")}</label>
              <input
                data-testid="prof-business" value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                className="w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3"
              />
            </div>
            <div>
              <label className="label-caps mb-1.5 block">{t("city")}</label>
              <select data-testid="prof-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3">
                {CITIES.map((c) => <option key={c} value={c}>{tCity(c)}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps mb-2 block">{t("categories")}</label>
              <p className="text-xs text-zinc-500 mb-2">{t("maxTwoCategories")}</p>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map((c) => {
                  const Icon = CATEGORY_ICONS[c]; const sel = form.categories.includes(c);
                  const disabled = !sel && form.categories.length >= MAX_MERCHANT_CATEGORIES;
                  return (
                    <button type="button" key={c} data-testid={`prof-cat-${c}`} onClick={() => toggleCat(c)}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-medium transition ${sel ? "border-emerald-600 bg-emerald-50 text-emerald-700" : disabled ? "border-zinc-100 bg-zinc-50 text-zinc-400" : "border-zinc-200 bg-white text-zinc-700"}`}>
                      <Icon className="h-4 w-4 shrink-0" />{tCat(c)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="label-caps mb-1.5 block">{t("whatsapp")}</label>
              <input data-testid="prof-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                className="w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3" />
            </div>
            <div>
              <label className="label-caps mb-1.5 block">{t("description")}</label>
              <textarea data-testid="prof-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3 py-2.5" />
            </div>
            <div>
              <label className="label-caps mb-2 block">{t("logo")}</label>
              <PhotoInput value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} label={t("upload")} testId="prof-logo" />
            </div>
            <button data-testid="prof-save" disabled={busy} className="btn-primary w-full">{busy ? "..." : t("save")}</button>
          </form>
        )}

        <button data-testid="logout-btn" onClick={doLogout} className="btn-secondary w-full mt-6">
          <LogOut className="h-4 w-4" />{t("logout")}
        </button>
      </div>
      <BottomNav />
    </div>
  );
}
